/**
 * Dynamic-download bundle contract (架构方案 §5.4.1, 见 12).
 *
 * A dynamically delivered module may declare a downloadable bundle: a self-contained
 * archive of its build artifacts (the「产物根目录」layout of 见 12 — the same
 * contents as `modules/<id>/dist`, manifest included). The platform's
 * BundleService downloads it, verifies it against the supply-chain gates
 * declared here (origin allowlist + sha256), and unpacks it into the
 * content-addressed cache (`userData/hibro-bundles/<id>@<version>`), which then
 * wins module-root resolution (`resolveModuleRoot`, 见 12).
 *
 * Pure, platform-free helpers live here so the validation policy is shared by
 * `validateManifest` (any process) and the main-process BundleService.
 */

// Type-only (erased at compile time), so this does not create a runtime cycle
// with `index.ts`, which re-exports this module.
import { splitSafeAssetPath } from './asset-path';
import type { ModuleEntry, ModuleFacets, ModuleRuntime } from './index';
import { parseVersion } from './semver';

/**
 * Minimal ambient surface of the WHATWG `URL` global. `@hibro/shared` compiles
 * against the pure `ESNext` lib (zero platform dependency — no DOM and no node
 * type libs), but `URL` is a standard global in every runtime the platform
 * supports (browsers, Node, Electron), so we declare just the members the
 * origin gate needs instead of widening the lib.
 */
declare class URL {
  constructor(input: string, base?: string);
  readonly protocol: string;
  readonly origin: string;
}

/**
 * Where to fetch a module's bundle and how to verify it (架构方案 §5.4.1/§5.6).
 * Declared in the module manifest; only consumed on the dynamic-download path (a
 * statically compiled module's code comes from the shell's build-time glob).
 */
export interface BundleDeclaration {
  /** Download location. Must be an http(s) URL on an allowlisted origin. */
  url: string;
  /**
   * Required content digest (hex sha256 of the archive bytes). The download is
   * discarded on mismatch — the integrity gate of §5.6 供应链.
   */
  sha256: string;
  /**
   * Optional expected size in bytes. When declared, the download is aborted as
   * soon as the stream exceeds it (cheap pre-digest guard against a tampered or
   * runaway response).
   */
  size?: number;
  /**
   * Publisher signature over the archive (§5.6 签名). Contract seam only for
   * now: carried and validated for shape, but not yet verified — signature
   * verification lands with the platform trust root (见 12 / 00 §8.3).
   */
  signature?: string;
}

/**
 * Where **one** lazy dist archive comes from (见 12 两阶段下载), as exactly one
 * of two sources:
 *
 * - **remote** `url` — an http(s) archive on an allowlisted origin; `sha256`
 *   is required (untrusted download, §5.6 供应链).
 * - **local** `path` — a module-root-relative archive shipped inside the module
 *   (self-carried `.tgz`); trusted like a sideload, so no origin allowlist and
 *   `sha256` is optional (verified only when declared).
 */
export interface DistBundleSource {
  /** Remote archive URL (http(s), allowlisted origin). Mutually exclusive with `path`. */
  url?: string;
  /** Module-root-relative local archive path (no `..`). Mutually exclusive with `url`. */
  path?: string;
  /** Hex sha256 of the archive bytes. Required for `url`, optional for `path`. */
  sha256?: string;
  /** Optional expected size in bytes (download aborts if exceeded, remote only). */
  size?: number;
  /** Publisher signature (§5.6). Contract seam only — carried, not yet verified. */
  signature?: string;
}

/**
 * A module's **lazy dist bundle** for two-phase delivery (见 12 两阶段下载). The
 * base package registers immediately; these heavy `dist/` artifacts are fetched
 * and unpacked into `<moduleRoot>/dist` on first activation.
 *
 * Exactly one of three forms (`validateManifest` enforces the xor):
 *
 * - a single **remote** `url` or **local** `path` — one archive serves every
 *   target ({@link DistBundleSource});
 * - a **`platform` map** — one archive per build target, keyed by
 *   {@link distBundleTargetKey}. An artifact that embeds a native runtime (an
 *   IDE, an Electron sub-app) is built once per `OS × CPU`, and `00` §3.2 says
 *   protocolizing the transport does not make the binary portable; without this
 *   map a manifest could only describe one of those builds, so multi-platform
 *   delivery had to be faked by publishing a different manifest per platform.
 *
 * The key carries the arch because that is the granularity artifacts are
 * actually produced at (`darwin-arm64` and `darwin-x64` are different bytes).
 * `entry.external.platform` uses the same {@link distBundleTargetKey}: a launch
 * command can differ per build target, and a main package that embeds the
 * binary has no `distBundle` map to read the supported targets from.
 *
 * Resolve with {@link resolveDistBundleTarget} rather than reading the fields:
 * that is the single place where "which archive does this machine want" is
 * answered, and where a miss stays a miss instead of falling back to some other
 * platform's payload.
 */
export interface DistBundleDeclaration extends DistBundleSource {
  /**
   * Per-build-target archives, keyed by {@link distBundleTargetKey}
   * (`${process.platform}-${process.arch}`, e.g. `win32-x64`, `darwin-arm64`).
   * Mutually exclusive with a top-level `url`/`path`.
   *
   * A target absent from the map means the module has no payload for that
   * machine — a real, reportable condition, not a reason to reach for another
   * entry.
   */
  platform?: Record<string, DistBundleSource>;
}

/**
 * The key a {@link DistBundleDeclaration.platform} map is addressed by:
 * `${platform}-${arch}`, taking the values Node reports in `process.platform`
 * and `process.arch` (`win32-x64`, `darwin-arm64`, `linux-x64`, …).
 *
 * Composed here so producers and the resolver cannot spell it differently.
 */
export function distBundleTargetKey(platform: string, arch: string): string {
  return `${platform}-${arch}`;
}

/** Whether a `platform` map key has the `<platform>-<arch>` shape. */
export function isDistBundleTargetKey(value: string): boolean {
  return /^[a-z0-9]+-[a-z0-9]+$/i.test(value);
}

/**
 * Whether this machine is one of the module's declared build targets (见 12).
 *
 * `targets` absent means the module does not restrict by architecture — the
 * common case, including a single entry script that picks a binary out of
 * `dist/` itself. A present list is the authority: launch and dist download
 * both stop here before reading `entry.external` or `distBundle`.
 */
export function supportsBuildTarget(
  targets: readonly string[] | undefined,
  platform: string,
  arch: string,
): boolean {
  if (targets === undefined) {
    return true;
  }
  return targets.includes(distBundleTargetKey(platform, arch));
}

/**
 * The single archive this machine should fetch, or `undefined` when the
 * declaration has nothing for it (见 12 两阶段下载).
 *
 * `undefined` is deliberately the whole failure story: callers report it (the
 * external-launch gate turns it into a `DistState` error) rather than fall back
 * to another target's archive, which would install e.g. a `darwin-arm64` build
 * on `win32-x64` and fail much later, at spawn, with an unrelated message.
 */
export function resolveDistBundleTarget(
  decl: DistBundleDeclaration,
  platform: string,
  arch: string,
): DistBundleSource | undefined {
  if (decl.platform) {
    return decl.platform[distBundleTargetKey(platform, arch)];
  }
  return decl.url !== undefined || decl.path !== undefined ? decl : undefined;
}

/**
 * A request to install a module bundle from the network (架构方案 §5.4.1, doc
 * 37). Unlike {@link BundleDeclaration} (embedded in a manifest the platform
 * already holds), an install request carries only where to fetch and how to
 * verify — the authoritative module manifest (id/version/...) is read from the
 * unpacked archive itself, so the installer needs nothing about the module
 * up-front.
 */
export interface BundleSource {
  /** Download location. Must be an http(s) URL on an allowlisted origin. */
  url: string;
  /** Required hex sha256 of the archive bytes (integrity gate, §5.6). */
  sha256: string;
  /** Optional expected size in bytes; the download aborts if exceeded. */
  size?: number;
  /** Publisher signature (§5.6). Contract seam only — carried, not yet verified. */
  signature?: string;
  /**
   * Optional HTTP headers the download GET must carry (they are part of a
   * presigned URL's signature when the store signed them). A bare GET when
   * omitted. `Host` and the other headers Chromium forbids the caller to set
   * are dropped: `net.fetch` throws `net::ERR_INVALID_ARGUMENT` if they are
   * passed, and Chromium supplies `Host` from the URL.
   */
  headers?: Record<string, string>;
}

/**
 * Default cap for a **network** bundle install: the compressed download and its
 * uncompressed unpack alike (架构方案 §5.4.1 / §5.6 hardening).
 *
 * Lives here rather than in either runtime's service so "both runtimes agree" is
 * structural instead of a coincidence. Local sideload is exempt on both — the
 * user explicitly picked the file.
 *
 * A deployment whose modules outgrow it raises it through
 * {@link BUNDLE_MAX_SIZE_KEY} rather than patching this. Note this is **not**
 * the lever for an IDE-scale payload: that is a desktop-only `external` module
 * (the web runtime has no `childProcess`, 见 19), and its heavy artifacts go
 * through `distBundle`'s far larger cap instead (见 12 两阶段下载).
 */
export const MAX_BUNDLE_BYTES = 256 * 1024 * 1024;

/**
 * Product-config key overriding {@link MAX_BUNDLE_BYTES}, in **megabytes** (见 05).
 *
 * One key for both runtimes, which is what makes each one's existing override
 * layer carry it for free: env/argv on desktop, `/_product.json` on web. The MB
 * unit follows `HIBRO_MAX_DIST_BUNDLE_MB`.
 */
export const BUNDLE_MAX_SIZE_KEY = 'bundle.maxSizeMB';

/**
 * Read {@link BUNDLE_MAX_SIZE_KEY} off a product-config map, in bytes.
 *
 * `undefined` when unset or unusable, letting the caller apply its own default —
 * a malformed value must not silently become a *smaller* cap than the one the
 * deployment thought it was setting. Accepts a string because env/argv overrides
 * arrive as one.
 */
export function resolveMaxBundleBytes(
  values: Record<string, unknown>,
): number | undefined {
  const raw = values[BUNDLE_MAX_SIZE_KEY];
  if (typeof raw !== 'number' && typeof raw !== 'string') {
    return undefined;
  }
  const mb = Number(raw);
  return Number.isFinite(mb) && mb > 0
    ? Math.floor(mb) * 1024 * 1024
    : undefined;
}

/**
 * The slice of a `Blob`/`File` the install path needs, so a picked local archive
 * can cross the channel **by reference**: structured clone carries a Blob without
 * copying its bytes, which is the difference between handing a 500 MB `.tgz` to
 * the host and copying it into the host's heap (见 12 本地资源侧载).
 *
 * Declared here, minimally, for the reason `URL` above is: this package compiles
 * against the pure `ESNext` lib and must not gain a DOM dependency.
 */
export interface BundleArchiveBlob {
  readonly size: number;
  stream(): {
    getReader(): {
      read(): Promise<{ done: boolean; value?: Uint8Array }>;
      releaseLock(): void;
    };
  };
}

/**
 * Options for uninstalling a dynamically delivered module (架构方案 §5.4.1,
 * 见 12). The module's code/contributions and its on-disk bundle cache are
 * always removed; `purgeData` additionally wipes the module's persisted data
 * (its isolated storage namespace + filesystem root + permission decisions).
 * When omitted/false, data is kept so a reinstall resumes where it left off
 * (the VSCode uninstall default).
 */
export interface UninstallOptions {
  /** Also delete the module's storage, fs root and permission decisions. */
  purgeData?: boolean;
}

/**
 * A dynamically-installed (downloaded) module as listed for the module manager
 * UI (架构方案 §5.4.1, 见 12). Derived from the authoritative manifest read out
 * of the unpacked bundle cache root, kept deliberately small (no on-disk paths
 * leak to the renderer).
 */
export interface InstalledModuleInfo {
  id: string;
  version: string;
  /**
   * The cache marker's digest (`.hibro-bundle.json`). For a network install it
   * is the verified archive sha256, so it can be compared with a catalog's
   * package digest. For a local sideload it is only a cache identity (见 12).
   */
  sha256: string;
  /**
   * The module's orthogonal execution facts (`deriveModuleFacets`), for display
   * (架构方案 §3/§5.8). Render with `describeModuleFacets`.
   */
  facets: ModuleFacets;
  /**
   * Optional human-facing name for the list label. Reserved seam: no producer
   * fills it today (the manifest has no such field), so consumers fall back to
   * `id`.
   */
  displayName?: string;
}

/**
 * 一个模块正在被装上、卸下，或在取它的 lazy `dist/`（架构方案 §5.4.1）。
 *
 * `fetching` 是 {@link DistState} 的投影：那是**已装模块**首次用到时才取的重产物（见 12
 * 两阶段下载）。收进同一张在途表，是因为对用户而言这三件事是同一句话——「这个模块还没法用，
 * 正在忙」——渲染端与模块端于是只读一个进度模型。
 */
export interface ModuleInstallProgress {
  /** 网络安装在解包前还不知道装的是谁（id 写在包内 manifest），此时为 undefined。 */
  moduleId?: string;
  phase: 'installing' | 'uninstalling' | 'fetching';
  /** 已收字节（driver 报得出时）。 */
  received?: number;
  /** 总字节（来源声明里带 `size` 时才有）。 */
  total?: number;
}

/**
 * 模块安装态的**全量**快照（{@link MODULES_CHANGED_EVENT} 的载荷）。
 *
 * 广播全量而不是增量：迟到的订阅者无需重放，这条与 detached 的
 * `platform.detached.changed` 是同一条规矩（见 08）。
 */
export interface ModuleInstallState {
  installed: InstalledModuleInfo[];
  inFlight: ModuleInstallProgress[];
  /**
   * The package actually running for each id, shipped floor or downloaded
   * overlay (见 12). Distinct from {@link installed}: that list is only the
   * uninstallable cache. A shipped module with no winning overlay is here and
   * not there.
   */
  running: RunningPackageInfo[];
}

/**
 * EventBus 事件：模块安装集合或在途安装态变化（架构方案 §5.4.1，见 12）。
 * 载荷是 {@link ModuleInstallState}。
 */
export const MODULES_CHANGED_EVENT = 'platform.modules.changed';

/** 渲染端安装/卸载模块的通道（见 12）。模块侧的对位入口是 `context.bundles`。 */
export const BUNDLE_CHANNEL = 'bundle';

/**
 * One file of an unpacked module tree, as handed across the install channel
 * after the page has picked a local directory (见 12). `segments` is the
 * module-root-relative path, already past {@link splitSafeAssetPath}.
 */
export interface BundleTreeFile {
  segments: string[];
  data: Uint8Array;
}

/**
 * 装/卸模块的服务面，渲染端经 {@link BUNDLE_CHANNEL} 调用。
 *
 * **刻意不挂在 `IModuleService` 上**：`IGatedModuleService` 继承后者，挂上去等于把「装模块」
 * 开给每一个沙箱 iframe/external。与 detach 走独立通道同理（见 08）。
 */
export interface IBundleService {
  /** 当前已装的下发模块 + 在途安装态（全量）。 */
  state(): Promise<ModuleInstallState>;
  /** 本运行端是否具备网络安装机械；false 时设置页应隐藏 url+sha256 表单。 */
  canInstall(): Promise<boolean>;
  /** 本运行端能否本地侧载；false 时设置页应隐藏「从 .tgz / 从目录」而不是等一次必然失败。 */
  canInstallLocal(): Promise<boolean>;
  /** 从网络源安装；返回装好的模块信息，装不成回 undefined（不记账）。 */
  install(source: BundleSource): Promise<InstalledModuleInfo | undefined>;
  /** 本地侧载（选一个 `.tgz` 或已解包目录）。取消返回 null；该运行端不支持时回 undefined。 */
  installLocal(
    kind: 'archive' | 'dir',
  ): Promise<InstalledModuleInfo | null | undefined>;
  /**
   * 已经挑好的本地 `.tgz` 装进内容寻址缓存（见 12）。选择器必须留在有手势的页面上，
   * 所以浏览器端先 pick 再调这个，而不是走 {@link IBundleService.installLocal}。
   *
   * 收的是 {@link BundleArchiveBlob} 而非字节：Blob 过 structuredClone 是按引用，
   * 一个 500 MB 的归档因此不会被拷进宿主堆。
   */
  installLocalArchive(
    archive: BundleArchiveBlob,
  ): Promise<InstalledModuleInfo | undefined>;
  /**
   * 已经挑好的解包目录装进内容寻址缓存。`files` 是产物根里的文件，不是工作目录整棵树。
   */
  installLocalDirectory(
    files: readonly BundleTreeFile[],
  ): Promise<InstalledModuleInfo | undefined>;
  uninstall(moduleId: string, options?: UninstallOptions): Promise<void>;
}

/**
 * 装/卸模块的模块侧接口，即 `context.bundles`（见 12）。落在与 {@link IBundleService} 同一个
 * 宿主单例上，所以模块侧与渲染端共享同一本账、同一条幂等规则。
 *
 * **不是人人都有**：见 {@link MODULE_INSTALL_PERMISSION}。
 */
export interface BundleApi {
  /** 已装的下发模块 + 在途安装态（全量）。变化订阅 {@link MODULES_CHANGED_EVENT}。 */
  state(): ModuleInstallState;
  /**
   * 本运行端**是否具备安装机械**。false 时该端根本装不了模块，调用方
   * 应当明说而不是摆一个必然失败的表单。浏览器端网络安装已具备；本地侧载见
   * {@link BundleApi.canInstallLocal}。
   */
  canInstall(): boolean;
  /** 本运行端能否本地侧载；false 时模块应隐藏该入口而不是等一个必然失败的调用。 */
  canInstallLocal(): boolean;
  install(source: BundleSource): Promise<InstalledModuleInfo | undefined>;
  /**
   * 下载并校验进缓存旁的 pending 目录，不热注册、不替换正在打开的模块。
   * 下次打开该模块时才提升进正式缓存并热注册。装不成回 `undefined`。
   */
  stage(source: BundleSource): Promise<InstalledModuleInfo | undefined>;
  /** POST 失败时丢掉 pending，避免下次打开该模块时装上一个服务端还没承认的包。 */
  discardStaged(moduleId: string): Promise<void>;
  /** 取消回 `null`，本运行端不支持回 `undefined`。 */
  installLocal(
    kind: 'archive' | 'dir',
  ): Promise<InstalledModuleInfo | null | undefined>;
  uninstall(moduleId: string, options?: UninstallOptions): Promise<void>;
}

/**
 * 「可以装模块」这项权限（架构方案 §5.6，见 12）。
 *
 * 它进 {@link PROTECTED_CAPABILITIES}，于是对沙箱面（iframe / external）fail-closed；
 * `ModuleManager` 再加一条不看声明的硬规则——**`dynamic` 的模块一律不给**（即
 * {@link ModuleFacets.isFirstParty} 为 false 者），因为声明是自述、来源不是。即：要装模块，
 * 必须**随应用发布**（非运行期装入）**且**显式声明。
 *
 * **这道门买到的是什么，要说清楚**：它挡得住沙箱面，也挡得住不带进程内代码的 dynamic 模块；
 * 但挡不住一个**下发的 `source` 模块**——那种模块本就在宿主进程内跑、有完整 Node 权限（见 12
 * 的既定信任姿态），不给 API 它也能自己动缓存目录。对那一类，真正的遏制只在安装前（sha256 +
 * 来源白名单 + 将来的签名验签）。所以这里是纵深防御与审计口径，不是运行时边界。
 */
export const MODULE_INSTALL_PERMISSION = 'modules:install';

/**
 * Env var carrying the comma-separated origin allowlist for bundle downloads
 * (§5.6 来源白名单). Read by the main process at startup; empty/unset means
 * **no origin is allowed** (fail closed — downloads are opt-in).
 */
export const ENV_BUNDLE_ALLOWED_ORIGINS = 'HIBRO_BUNDLE_ALLOWED_ORIGINS';

/** Directory under `userData` holding the bundle cache (架构方案 §5.4.1). */
export const BUNDLE_CACHE_DIRNAME = 'hibro-bundles';

/**
 * Module-root subdirectories that make up a module's **delivered shape** (见 12
 * 分层约定): eager `assets/` and `nls/` beside the lazy `dist/`, always
 * accompanied by `module.manifest.json` at the root.
 *
 * Equivalently: the directories a module ships, and nothing it merely builds
 * from. A prebuilt `entry.service` that lives outside them is staged as its own
 * file ({@link stagedPrebuiltServicePaths}): the loader reads it from the module
 * root at activation, and `dist/` may not be there yet. `src/`, `tsconfig` and
 * above all `node_modules/` stay out — which is what makes this a rule rather
 * than a listing, and why it is stated once: forge's `prePackage` staging, the
 * web build's `_mod/<id>/` copy and the browser's load-unpacked sideload all
 * follow it, and the last of those copies a **developer's working directory**,
 * where getting it wrong means walking a dependency tree into OPFS.
 *
 * `'dist'` is spelled out rather than referencing `MODULE_OUTPUT_DIR`, which
 * lives in the package barrel and would make this module import its own
 * re-exporter. forge likewise keeps an inlined copy on purpose — its config is
 * loaded before this package can be imported as TS source — so both are mirrors
 * to update by hand if this ever changes. The prebuilt-service decision has its
 * own mirror in `scripts/staged-service-files.mjs` for the same reason.
 */
export const MODULE_STAGED_SUBDIRS = ['assets', 'nls', 'dist'] as const;

/**
 * Prebuilt `entry.service` files staging must copy beside the manifest (见 12).
 *
 * One runtime, so a bare string (the same file on every runtime) and a
 * per-runtime map contribute at most one path. The shell compiles `src/…`
 * itself, and {@link MODULE_STAGED_SUBDIRS} already copies the first segment
 * of anything under `assets/`, `nls/` or `dist/`. What remains — a `service.js`
 * at the module root — is a file the loader reads at activation.
 *
 * A declaration that does not stay inside the module root throws. Skipping it
 * would ship a package whose activation cannot find the file it named.
 * Whether that file is actually on disk is the copy site's check.
 *
 * `scripts/staged-service-files.mjs` repeats this function for forge, which
 * cannot import this package as TS source. The two are locked together by
 * `scripts/staged-service-files.test.ts`.
 */
export function stagedPrebuiltServicePaths(
  entry: ModuleEntry | undefined,
  runtime: ModuleRuntime,
): string[] {
  const service = entry?.service;
  const raw = typeof service === 'string' ? service : service?.[runtime];
  if (typeof raw !== 'string') {
    return [];
  }
  const declared = raw.trim();
  if (!declared) {
    return [];
  }
  const segments = splitSafeAssetPath(declared);
  if (!segments || segments.length === 0) {
    throw new Error(
      `entry.service ${JSON.stringify(declared)} is not a path inside the module root`,
    );
  }
  const head = segments[0];
  if (
    head === 'src' ||
    (MODULE_STAGED_SUBDIRS as readonly string[]).includes(head)
  ) {
    return [];
  }
  return [segments.join('/')];
}

/**
 * Directory under `userData` holding relocated lazy `dist/` trees for packaged
 * modules that declare a `distBundle` (见 12 两阶段下载). A packaged module's
 * root lives read-only-ish inside the installer-managed app dir
 * (`resources/modules/<id>`); unpacking a large `dist/` (e.g. an IDE) there is
 * both a Windows long-path deletion hazard for the installer and — on macOS —
 * writes into the signed `.app` bundle. So such modules unpack into
 * `<userData>/hibro-dist/<id>/dist` instead, keyed by module id (the shipped
 * `.tgz` stays in `resources`, only the unpacked tree relocates). Non-packaged
 * delivery (dev / downloaded / linked) keeps `dist/` at the module root.
 */
export const DIST_CACHE_DIRNAME = 'hibro-dist';

/**
 * Completion marker written inside an unpacked bundle root by the
 * BundleService. Its presence (with the content-addressed dir name matching
 * its `id@version`) is what makes a cache entry valid: unpacking stages into a
 * tmp dir and atomically renames, so a crash mid-download/unpack never leaves
 * a half-populated root that resolves as a module. The shape lives here (with
 * {@link parseBundleMarker}) so the main process (writer/scanner) and the
 * extension host (bundle module discovery, 见 12) share one policy.
 */
/**
 * Parse JSON text from an unpacked bundle (manifest / marker / nls).
 * Strips a leading UTF-8 BOM (`U+FEFF`) so Windows packers
 * (`Set-Content -Encoding utf8`, `Out-File`) do not fail as invalid JSON —
 * Node's `utf8` decoder keeps the BOM, and `JSON.parse` rejects it.
 */
export function parseJsonText(text: string): unknown {
  return JSON.parse(text.startsWith('\uFEFF') ? text.slice(1) : text);
}

export const BUNDLE_MARKER_FILENAME = '.hibro-bundle.json';

/** Contents of {@link BUNDLE_MARKER_FILENAME}. */
export interface BundleMarker {
  id: string;
  version: string;
  /** Hex sha256 of the downloaded archive (lowercased). */
  sha256: string;
  downloadedAt: string;
}

/**
 * Completion marker written inside a module's `dist/` after a lazy dist bundle
 * (见 12 两阶段下载) is fetched and unpacked. Its `sha256` (the declared archive
 * digest) lets `ensureDistBundle` skip re-downloading when the on-disk `dist/`
 * already matches `manifest.distBundle.sha256`, and re-fetch when it changes.
 */
export const DIST_BUNDLE_MARKER_FILENAME = '.hibro-dist.json';

/** Contents of {@link DIST_BUNDLE_MARKER_FILENAME}. */
export interface DistBundleMarker {
  /** Hex sha256 of the fetched dist archive (lowercased), from the declaration. */
  sha256: string;
  downloadedAt: string;
}

/**
 * IPC channel the main process broadcasts a module's lazy-dist fetch state on
 * (见 12 两阶段下载): `fetching` (with byte progress) → `ready` | `error`. The
 * renderer subscribes (preload `hibroBundle.onDistState`) to overlay a loading
 * spinner on the activityBar item while its `dist/` is being downloaded/unpacked.
 */
export const DIST_STATE_IPC = 'hibro:dist:state';

/** Payload of {@link DIST_STATE_IPC}: a module's lazy-dist fetch state. */
export interface DistState {
  moduleId: string;
  phase: 'fetching' | 'ready' | 'error';
  /** Bytes received so far (phase `fetching`). */
  received?: number;
  /** Total bytes if known from the declaration's `size` (phase `fetching`). */
  total?: number;
  /** Human-readable failure reason (phase `error`). */
  message?: string;
}

/** Validate a parsed dist marker JSON value (fail closed on any malformed shape). */
export function parseDistBundleMarker(
  raw: unknown,
): DistBundleMarker | undefined {
  if (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as DistBundleMarker).sha256 === 'string'
  ) {
    return raw as DistBundleMarker;
  }
  return undefined;
}

/** Validate a parsed marker JSON value (fail closed on any malformed shape). */
export function parseBundleMarker(raw: unknown): BundleMarker | undefined {
  if (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as BundleMarker).id === 'string' &&
    typeof (raw as BundleMarker).version === 'string' &&
    typeof (raw as BundleMarker).sha256 === 'string'
  ) {
    return raw as BundleMarker;
  }
  return undefined;
}

/**
 * Content-address key for a cached bundle (`<id>@<version>`, 架构方案 §5.4.1).
 * Guards path safety: id/version come from an untrusted manifest, so anything
 * outside `[\w.-]` (path separators, traversal dots beyond `.`, etc.) throws.
 */
export function bundleCacheKey(id: string, version: string): string {
  for (const part of [id, version]) {
    // `[\w.-]+` already rejects separators and the empty string, but `.` and
    // `..` match it — those are the traversal names that would escape a cache
    // dir if a caller ever joined `id` (not the composed key) as a segment.
    if (!/^[\w.-]+$/.test(part) || part === '.' || part === '..') {
      throw new Error(`bundle: unsafe cache key component "${part}"`);
    }
  }
  return `${id}@${version}`;
}

/**
 * Marker written beside a shipped module's manifest (见 12). Its `sha256` is
 * the digest of the tgz {@link writeShippedMarker} would produce for that
 * root, so a catalog can compare it with a store package. Absent from the
 * tgz itself.
 */
export const SHIPPED_MARKER_FILENAME = '.hibro-shipped.json';

/** Contents of {@link SHIPPED_MARKER_FILENAME}. */
export interface ShippedMarker {
  id: string;
  version: string;
  /** Hex sha256 of the shipped module archive (lowercased). */
  sha256: string;
}

/** A shipped module's identity, digest optional until the marker is written. */
export interface ShippedPackageFact {
  id: string;
  version: string;
  sha256?: string;
}

/**
 * Which package of an id is running (见 12). `downloaded` is a cache entry
 * that beat the shipped floor. `shipped` is the copy in the app package.
 */
export interface RunningPackageInfo {
  id: string;
  version: string;
  /** Archive digest when this copy has one. Sideload digests are cache identity. */
  sha256?: string;
  origin: 'shipped' | 'downloaded';
}

/** Validate a shipped-marker JSON value (fail closed on any malformed shape). */
export function parseShippedMarker(raw: unknown): ShippedMarker | undefined {
  if (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as ShippedMarker).id === 'string' &&
    typeof (raw as ShippedMarker).version === 'string' &&
    typeof (raw as ShippedMarker).sha256 === 'string' &&
    isValidSha256((raw as ShippedMarker).sha256)
  ) {
    return {
      id: (raw as ShippedMarker).id,
      version: (raw as ShippedMarker).version,
      sha256: (raw as ShippedMarker).sha256.toLowerCase(),
    };
  }
  return undefined;
}

/**
 * A cached package replaces the shipped copy of the same id only when its
 * semver is strictly newer (见 12). No shipped version means there is no
 * floor, so the cache is the only copy.
 */
export function cacheSupersedesShipped(
  cachedVersion: string,
  shippedVersion: string | undefined,
): boolean {
  if (shippedVersion === undefined) {
    return true;
  }
  return isNewerVersion(cachedVersion, shippedVersion);
}

/** Why an install was refused: the package does not beat the shipped floor. */
export function bundleNotNewerThanShippedMessage(
  moduleId: string,
  version: string,
  shippedVersion: string,
): string {
  return `bundle: ${moduleId}@${version} is not newer than shipped ${shippedVersion}`;
}

/**
 * The package actually running for each id (见 12). A download wins only when
 * {@link cacheSupersedesShipped} says so; otherwise the shipped floor does.
 * Ids present on only one side pass through. Order is by id.
 */
export function resolveRunningPackages(
  shipped: readonly ShippedPackageFact[],
  downloaded: readonly { id: string; version: string; sha256: string }[],
): RunningPackageInfo[] {
  const shippedById = new Map(shipped.map((pkg) => [pkg.id, pkg]));
  const downloadedById = new Map(downloaded.map((pkg) => [pkg.id, pkg]));
  const ids = [...new Set([...shippedById.keys(), ...downloadedById.keys()])];
  ids.sort((a, b) => a.localeCompare(b));
  const running: RunningPackageInfo[] = [];
  for (const id of ids) {
    const floor = shippedById.get(id);
    const overlay = downloadedById.get(id);
    if (overlay && cacheSupersedesShipped(overlay.version, floor?.version)) {
      running.push({
        id,
        version: overlay.version,
        sha256: overlay.sha256,
        origin: 'downloaded',
      });
      continue;
    }
    if (floor) {
      running.push({
        id,
        version: floor.version,
        ...(floor.sha256 ? { sha256: floor.sha256 } : {}),
        origin: 'shipped',
      });
      continue;
    }
    if (overlay) {
      running.push({
        id,
        version: overlay.version,
        sha256: overlay.sha256,
        origin: 'downloaded',
      });
    }
  }
  return running;
}

/** Whether `a` is a strictly newer semver than `b` (unparseable sorts lowest). */
export function isNewerVersion(a: string, b: string): boolean {
  const va = parseVersion(a);
  const vb = parseVersion(b);
  if (!va || !vb) {
    return !!va;
  }
  if (va.major !== vb.major) {
    return va.major > vb.major;
  }
  if (va.minor !== vb.minor) {
    return va.minor > vb.minor;
  }
  return va.patch > vb.patch;
}

/**
 * Custom scheme serving non-source module assets from their on-disk module
 * root (见 12 / §5.8): `hibro-assets://<moduleId>/<root-relative path>`.
 * The main process registers a privileged protocol handler that maps the host
 * to the module's resolved root (downloaded cache → shipped → dev) and serves
 * files from inside it.
 *
 * **The module id must stay in the authority** (见 20): that is what gives each
 * module its own origin, which is both the iframe isolation boundary and the
 * `targetOrigin` the gated SDK port transfer is delivered to. Moving it into the
 * path would collapse every module onto one origin.
 */
export const ASSET_SCHEME = 'hibro-assets';

/**
 * Default deep-link scheme (见 20): `hibro://<moduleId>/<rest>`.
 *
 * This is the **stable-channel default**. The running app may use a channelized
 * value (`hibro-dev`, …) resolved by `resolveAppIdentity` and injected into the
 * product-config map as `app.scheme`; parsers take that scheme as a parameter
 * ({@link parseModuleLink}). Deliberately separate from {@link ASSET_SCHEME} —
 * only the deep-link scheme is registered with the OS.
 */
export const APP_SCHEME = 'hibro';

/**
 * Scheme the **packaged** desktop app loads its own workbench page from
 * (见 20): `hibro-app://workbench/index.html`, in place of `file://`.
 *
 * A `file://` document has an opaque origin, so nothing can name the workbench
 * precisely — a third-party page that wants to allow being framed by the client
 * has to put the whole `file:` scheme in its `frame-ancestors`, which allows any
 * local HTML file. Giving the page a real authority makes it exactly one
 * addressable origin. Same move as VS Code's `vscode-file://vscode-app`.
 *
 * Like {@link ASSET_SCHEME} and unlike {@link APP_SCHEME}, it is **never
 * channelized and never registered with the OS**: it addresses files inside the
 * installation, so an OS-recognized form would let any web page hand the app one.
 *
 * Dev keeps loading from the Vite dev server (HMR needs a real http origin), so
 * the workbench has two possible origins by design — see {@link WORKBENCH_PAGE_URL}.
 */
export const APP_PAGE_SCHEME = 'hibro-app';

/**
 * The only authority {@link APP_PAGE_SCHEME} serves (见 20). Fixed rather than
 * per-window: every workbench window loads the same document and differs only by
 * query (a detached one adds `?surface=<viewId>`, 见 08), so a second authority
 * would buy nothing and only dilute the origin.
 */
export const APP_PAGE_HOST = 'workbench';

/** Entry document of the packaged renderer build, served at the scheme root. */
export const APP_PAGE_ENTRY = 'index.html';

/**
 * Absolute URL of the packaged workbench page (见 20). The dev counterpart is
 * the Vite dev server URL, which is why anything allow-listing the workbench
 * (a third-party `frame-ancestors`, 见 09) needs both entries.
 */
export const WORKBENCH_PAGE_URL = `${APP_PAGE_SCHEME}://${APP_PAGE_HOST}/${APP_PAGE_ENTRY}`;

/**
 * Reserved first path segment that routes an asset-scheme request to a module's
 * isolated **storage** root instead of its code/asset root (见 12 / §5.13):
 * `<assetBase><moduleId>/@storage/<storage-relative path>`. Single source of
 * truth for main-process routing, the web Service Worker, and module-side URL
 * builders (e.g. IM `im.mediaFileUrl`).
 *
 * Note this marker lives *inside* a module's own namespace, so it is unrelated
 * to {@link PLATFORM_PATH_PREFIX}, which separates platform paths from module
 * ids one level up (见 20).
 */
export const MODULE_STORAGE_URL_PREFIX = '@storage';

/**
 * Reserved first path segment addressing a file **outside every module root**
 * via an unguessable token: `<assetBase><moduleId>/@local/<token>` (见 12 /
 * §5.13). The counterpart to {@link MODULE_STORAGE_URL_PREFIX} for files the
 * app must render but does not own — chiefly IM's zero-copy send, where the
 * file stays where the user has it and is never copied inward
 * (`modules/im/docs/architecture/06-媒体与文件链路.md` §1.6).
 *
 * **The token is the access control, not the path.** A renderer that can
 * assemble a URL cannot name an arbitrary file: the path lives only in a
 * main-process table, minted when a trusted service asks
 * {@link ModuleContext.localFileUrl} for an absolute path it already holds
 * (IM looks that path up from a `file_id`). Serving still refuses anything
 * whose extension is not an image or video (`isServableLocalMedia`).
 *
 * **Desktop only.** A browser runtime has no OS paths, so its static route /
 * Service Worker answers this prefix with 404 (见 18); `context.localFileUrl`
 * yields `undefined` there so callers degrade rather than build a dead URL.
 */
export const MODULE_LOCAL_URL_PREFIX = '@local';

/**
 * Prefix a module's assets are addressed under. Must end with a separator: the
 * resolved URL is `<base><moduleId>/<path>`.
 *
 * Desktop uses the privileged `hibro-assets://` scheme ({@link DEFAULT_ASSET_BASE}),
 * served by the main-process protocol handler. A browser runtime cannot register
 * a custom scheme, so it passes an http(s) base (e.g. `<origin>/_mod/`) whose
 * requests are answered by a static route / Service Worker — see 18 Web 运行端.
 */
export type AssetBase = string;

/** Desktop default: the privileged custom scheme (见 12 / §5.8). */
export const DEFAULT_ASSET_BASE: AssetBase = `${ASSET_SCHEME}://`;

/**
 * Split a module-root-relative path into safe segments, or `undefined` if it
 * could address anything but a file inside that root. Accepts posix or Windows
 * separators; drops empty and `.` segments; rejects an empty result, `..`, and
 * C0 control characters.
 *
 * The single rule for "is this a usable module-relative path", shared by every
 * consumer of one — {@link buildModuleStorageUrl} composing a URL, and
 * `capabilities.saveStorageFile` handing a file to the user (见 18). Both must
 * fail closed on exactly the same inputs, which they only do by construction if
 * the test lives in one place.
 */
export function splitModuleRelativePath(
  relativePath: string,
): string[] | undefined {
  if (!relativePath) {
    return undefined;
  }
  const segments = relativePath
    .replace(/\\/g, '/')
    .split('/')
    .filter((segment) => segment !== '' && segment !== '.');
  if (
    segments.length === 0 ||
    segments.some(
      (segment) =>
        segment === '..' ||
        // eslint-disable-next-line no-control-regex -- reject C0 controls in URL path segments
        /[\u0000-\u001f]/.test(segment),
    )
  ) {
    return undefined;
  }
  return segments;
}

/**
 * Build `<assetBase><moduleId>/@storage/<encoded-rel>` from a
 * storage-root-relative path, confined by {@link splitModuleRelativePath}. Fail
 * closed: empty moduleId/path, `..`, or control characters → `undefined`.
 * Callers must already confine the absolute file under the module storage root
 * (`path.relative` / equivalent) before passing the relative path here — this
 * helper only encodes and composes.
 *
 * Segments **are** percent-encoded here, unlike {@link resolveViewUrl}: storage
 * holds runtime filenames the module never chose (IM's media), while the code
 * root holds build outputs whose names its author controls.
 *
 * `assetBase` is the same seam {@link resolveViewUrl} uses (见 18): desktop keeps
 * the default `hibro-assets://…` shape; the browser passes
 * `<origin>/_mod/` so both ends share one relative path and differ only in the
 * base the host injects.
 */
export function buildModuleStorageUrl(
  moduleId: string,
  relativePath: string,
  assetBase: AssetBase = DEFAULT_ASSET_BASE,
): string | undefined {
  const segments = moduleId ? splitModuleRelativePath(relativePath) : undefined;
  if (!segments) {
    return undefined;
  }
  const encoded = segments
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  const base = assetBase.endsWith('/') ? assetBase : `${assetBase}/`;
  return `${base}${moduleId}/${MODULE_STORAGE_URL_PREFIX}/${encoded}`;
}

/**
 * Whether `segment` is an `@local` grant token (base64url alphabet, 16–64
 * chars). The main process mints these; this check is only the URL-shape
 * half so a malformed request 404s before the table is consulted.
 */
export function isLocalFileToken(segment: string): boolean {
  return /^[A-Za-z0-9_-]{16,64}$/.test(segment);
}

/**
 * Build `<assetBase><moduleId>/@local/<token>` (见 {@link MODULE_LOCAL_URL_PREFIX}).
 * Fail closed on an empty moduleId or a segment that is not a token — composing
 * a URL that could never be served is how a typo becomes a silent 404 later.
 *
 * The token is minted by the main process (`LocalFileGrantTable`); this helper
 * only composes. Whether the runtime can serve such a URL at all is the
 * caller's question (`context.localFileUrl` answers `undefined` on web).
 */
export function buildModuleLocalFileUrl(
  moduleId: string,
  token: string,
  assetBase: AssetBase = DEFAULT_ASSET_BASE,
): string | undefined {
  if (!moduleId || !isLocalFileToken(token)) {
    return undefined;
  }
  const base = assetBase.endsWith('/') ? assetBase : `${assetBase}/`;
  return `${base}${moduleId}/${MODULE_LOCAL_URL_PREFIX}/${token}`;
}

/**
 * Whether a declared URL already carries a scheme (`data:`, `https:`, `hibro:`, …)
 * and is therefore absolute rather than module-relative.
 *
 * Shared so the two consumers cannot drift: {@link resolveViewUrl} passes such a
 * value through (a view may legitimately declare `https://…`), while
 * `ModuleContext.assetUrl` **rejects** it (that API means "my own shipped file",
 * so echoing back a caller-supplied URL would be a footgun). Opposite decisions,
 * one test.
 */
export function hasUrlScheme(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value);
}

/**
 * Resolve a view contribution's `url` to the absolute URL the renderer loads
 * (见 12 / §5.8). Absolute URLs (`data:`/`http(s):`/anything with a
 * scheme) pass through untouched; a bare relative path resolves against the
 * owning module's output root as `<assetBase><moduleId>/<path>` (见 12 产物根目录
 * 约定). Fail closed: a traversing/absolute *path* yields `undefined` rather
 * than a URL that could escape the module root.
 *
 * `assetBase` is the single seam through which a runtime chooses how module
 * assets are addressed (见 18): this function is the **only** producer of module
 * asset URLs — view `url`s, `runtimeUiEntries` and (via {@link normalizeIconSpec})
 * icons all funnel through it — so threading a base here covers every consumer.
 * A base without a trailing separator gets one, so `<origin>/mod` and
 * `<origin>/mod/` behave the same.
 */
export function resolveViewUrl(
  url: string | undefined,
  moduleId: string,
  assetBase: AssetBase = DEFAULT_ASSET_BASE,
): string | undefined {
  if (url === undefined) {
    return undefined;
  }
  // Anything with a scheme (data:, https:, hibro:, ...) is already absolute.
  if (hasUrlScheme(url)) {
    return url;
  }
  const segments = url
    .replace(/\\/g, '/')
    .split('/')
    .filter((segment) => segment !== '' && segment !== '.');
  if (
    segments.length === 0 ||
    url.startsWith('/') ||
    segments.some((segment) => segment === '..')
  ) {
    return undefined;
  }
  const base = assetBase.endsWith('/') ? assetBase : `${assetBase}/`;
  return `${base}${moduleId}/${segments.join('/')}`;
}

/** Parse the allowlist env value: comma-separated, trimmed, empties dropped. */
export function parseAllowedOrigins(raw: string | undefined): string[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/** Whether `value` parses as an http(s) URL (the only schemes bundles allow). */
export function isHttpUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:';
}

/** Whether `value` is a well-formed hex sha256 digest (64 hex chars). */
export function isValidSha256(value: string): boolean {
  return /^[0-9a-f]{64}$/i.test(value);
}

/**
 * The one allowlist entry that is not an origin: "this channel does not pin the
 * download source" (见 12 / 05).
 *
 * It exists because the alternative is worse. A deployment whose bundles come
 * from presigned, rotating object-storage hosts cannot enumerate origins ahead
 * of time, and without a way to say so in the *config*, the pressure lands on
 * the code — as it did: `product.dev.json` already carried `["*"]` expecting it
 * to mean this, the matcher rejected it (`new URL('*')` throws), and somebody
 * short-circuited this function with an unconditional `return true` instead,
 * which took the allowlist out for every channel including packaged builds.
 *
 * A wildcard *entry* keeps that decision where it can be reviewed: it is written
 * per channel, `product.json` stays `[]`, and reading the config tells you which
 * builds pin their source and which do not.
 */
const ANY_ORIGIN = '*';

/**
 * Whether a bundle URL's origin is on the allowlist (§5.6 来源白名单). Entries
 * are compared by URL origin (scheme + host + port), so `https://cdn.example`
 * and `https://cdn.example/some/path` allow the same set. Fail closed: a
 * malformed URL, a non-http(s) scheme, an empty allowlist or a malformed
 * allowlist entry never match.
 *
 * {@link ANY_ORIGIN} is the one entry that matches any origin. It never relaxes
 * the scheme gate — `file:` / `ftp:` stay rejected — and it never stands in for
 * the sha256 check, which is what actually decides whether the bytes are the
 * bytes that were asked for.
 */
export function isOriginAllowed(
  url: string,
  allowedOrigins: readonly string[],
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return false;
  }
  if (allowedOrigins.includes(ANY_ORIGIN)) {
    return true;
  }

  return allowedOrigins.some((entry) => {
    try {
      return new URL(entry).origin === parsed.origin;
    } catch {
      return false;
    }
  });
}
